import React, { useState, useMemo } from 'react';
// Stub drag-and-drop components (preview-only, no dependency needed)
const DragDropContext = ({ children }) => <>{children}</>;
const Droppable = ({ children }) => children({ innerRef: () => {}, droppableProps: {}, placeholder: null }, { isDraggingOver: false });
const Draggable = ({ children }) => children({ innerRef: () => {}, draggableProps: {}, dragHandleProps: {} }, { isDragging: false });
import Icon from './ui-stubs';
import { format } from 'date-fns';

const KanbanView = ({ 
  tasks, 
  onTaskClick, 
  onDragEnd, 
  canTransition, 
  getRiskLevel, 
  isOverdue, 
  calculateBlockedDuration, 
  allTasks,
  statusConfig = null // Configuration-driven columns
}) => {
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);

  // Default status configuration if not provided
  const defaultStatusConfig = [
    { 
      id: 'To Do', 
      label: 'To Do', 
      color: '#6B7280',
      theme: 'bg-gray-100',
      allowedTransitions: ['In Progress', 'Blocked'],
      isTerminal: false,
      wipLimit: null,
      description: 'Tasks ready to be started'
    },
    { 
      id: 'In Progress', 
      label: 'In Progress', 
      color: '#3B82F6',
      theme: 'bg-blue-100',
      allowedTransitions: ['In Review', 'Blocked', 'To Do'],
      isTerminal: false,
      wipLimit: 5,
      description: 'Tasks currently being worked on'
    },
    { 
      id: 'In Review', 
      label: 'In Review', 
      color: '#8B5CF6',
      theme: 'bg-purple-100',
      allowedTransitions: ['Done', 'In Progress', 'Blocked'],
      isTerminal: false,
      wipLimit: 3,
      description: 'Tasks awaiting review and approval'
    },
    { 
      id: 'Blocked', 
      label: 'Blocked', 
      color: '#EF4444',
      theme: 'bg-red-100',
      allowedTransitions: ['To Do', 'In Progress'],
      isTerminal: false,
      wipLimit: null,
      description: 'Tasks blocked by dependencies or issues'
    },
    { 
      id: 'Done', 
      label: 'Done', 
      color: '#10B981',
      theme: 'bg-green-100',
      allowedTransitions: ['In Review'],
      isTerminal: true,
      wipLimit: null,
      description: 'Completed tasks'
    },
  ];

  const columns = (Array.isArray(statusConfig) ? statusConfig : null) || defaultStatusConfig;

  const getTasksByStatus = (status) => {
    return tasks?.filter(task => task?.status === status) || [];
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'border-l-4 border-l-red-600';
      case 'High':
        return 'border-l-4 border-l-orange-500';
      case 'Medium':
        return 'border-l-4 border-l-yellow-500';
      case 'Low':
        return 'border-l-4 border-l-green-500';
      default:
        return 'border-l-4 border-l-gray-400';
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd');
    } catch {
      return dateString;
    }
  };

  const hasUnresolvedDependencies = (task) => {
    if (!task?.dependencies || task?.dependencies?.length === 0) return false;
    return task?.dependencies?.some(depId => {
      const depTask = allTasks?.find(t => t?.id === depId);
      return depTask && depTask?.status !== 'Done';
    });
  };

  const getBlockReason = (task) => {
    if (task?.status === 'Blocked') {
      const blockedDays = calculateBlockedDuration(task);
      return `Blocked for ${blockedDays} day${blockedDays !== 1 ? 's' : ''}`;
    }
    if (hasUnresolvedDependencies(task)) {
      const unresolvedCount = task?.dependencies?.filter(depId => {
        const depTask = allTasks?.find(t => t?.id === depId);
        return depTask && depTask?.status !== 'Done';
      })?.length;
      return `${unresolvedCount} unresolved dependenc${unresolvedCount !== 1 ? 'ies' : 'y'}`;
    }
    return null;
  };

  const isTaskBlocked = (task) => {
    return task?.status === 'Blocked' || hasUnresolvedDependencies(task);
  };

  const getRiskBadge = (task) => {
    const risk = getRiskLevel(task);
    switch (risk) {
      case 'critical':
        return { color: 'bg-red-600 text-white', icon: 'AlertTriangle', label: 'Critical' };
      case 'high':
        return { color: 'bg-orange-600 text-white', icon: 'AlertCircle', label: 'High Risk' };
      case 'medium':
        return { color: 'bg-yellow-600 text-white', icon: 'AlertCircle', label: 'Medium Risk' };
      default:
        return null;
    }
  };

  // Column-level intelligence
  const getColumnRiskSummary = (columnTasks) => {
    const riskCounts = { critical: 0, high: 0, medium: 0 };
    columnTasks?.forEach(task => {
      const risk = getRiskLevel(task);
      if (risk === 'critical' || risk === 'high' || risk === 'medium') {
        riskCounts[risk]++;
      }
    });
    return riskCounts;
  };

  const isWIPLimitExceeded = (column, taskCount) => {
    return column?.wipLimit && taskCount >= column?.wipLimit;
  };

  const canDropInColumn = (task, column) => {
    if (!draggedTask) return true;
    
    // Check if task is blocked
    if (isTaskBlocked(task) && column?.id !== 'Blocked') {
      return false;
    }

    // Check transition validity
    const transition = canTransition(task, task?.status, column?.id);
    if (!transition?.allowed) {
      return false;
    }

    // Check WIP limit
    const columnTasks = getTasksByStatus(column?.id);
    if (isWIPLimitExceeded(column, columnTasks?.length + 1)) {
      return false;
    }

    return true;
  };

  const getDropFeedbackMessage = (task, column) => {
    if (isTaskBlocked(task) && column?.id !== 'Blocked') {
      return getBlockReason(task);
    }

    const transition = canTransition(task, task?.status, column?.id);
    if (!transition?.allowed) {
      return transition?.reason;
    }

    const columnTasks = getTasksByStatus(column?.id);
    if (isWIPLimitExceeded(column, columnTasks?.length + 1)) {
      return `WIP limit (${column?.wipLimit}) would be exceeded`;
    }

    return null;
  };

  const handleDragStart = (result) => {
    const task = tasks?.find(t => t?.id === result?.draggableId);
    setDraggedTask(task);
  };

  const handleDragEnd = (result) => {
    setDraggedTask(null);
    setHoveredColumn(null);

    if (!result?.destination) return;

    const taskId = result?.draggableId;
    const fromStatus = result?.source?.droppableId;
    const toStatus = result?.destination?.droppableId;

    if (fromStatus === toStatus) return;

    const task = tasks?.find(t => t?.id === taskId);
    if (!task) return;

    // Final validation before drop
    if (!canDropInColumn(task, columns?.find(c => c?.id === toStatus))) {
      return; // Silently reject invalid drop
    }

    onDragEnd(result);
  };

  return (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex space-x-4 overflow-x-auto pb-4" role="region" aria-label="Kanban board">
        {columns?.map((column) => {
          const columnTasks = getTasksByStatus(column?.id);
          const riskSummary = getColumnRiskSummary(columnTasks);
          const wipExceeded = isWIPLimitExceeded(column, columnTasks?.length);
          const canDrop = draggedTask ? canDropInColumn(draggedTask, column) : true;
          const dropMessage = draggedTask ? getDropFeedbackMessage(draggedTask, column) : null;

          return (
            <div key={column?.id} className="flex-shrink-0 w-80">
              {/* Column Header with Intelligence */}
              <div 
                className={`${column?.theme} rounded-t-lg px-4 py-3 border-b-2 border-border`}
                style={{ borderBottomColor: column?.color }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900">{column?.label}</h3>
                    <span 
                      className={`text-xs font-medium px-2 py-1 rounded-full ${
                        wipExceeded ? 'bg-red-600 text-white' : 'bg-white text-gray-700'
                      }`}
                      title={column?.wipLimit ? `WIP Limit: ${column?.wipLimit}` : 'No WIP limit'}
                    >
                      {columnTasks?.length}
                      {column?.wipLimit && ` / ${column?.wipLimit}`}
                    </span>
                  </div>
                  {column?.isTerminal && (
                    <Icon name="CheckCircle" size={16} className="text-green-600" title="Terminal status" />
                  )}
                </div>

                {/* Risk Aggregation */}
                {(riskSummary?.critical > 0 || riskSummary?.high > 0 || riskSummary?.medium > 0) && (
                  <div className="flex items-center space-x-2 text-xs">
                    {riskSummary?.critical > 0 && (
                      <span className="flex items-center space-x-1 text-red-700 font-medium">
                        <Icon name="AlertTriangle" size={12} />
                        <span>{riskSummary?.critical} critical</span>
                      </span>
                    )}
                    {riskSummary?.high > 0 && (
                      <span className="flex items-center space-x-1 text-orange-700 font-medium">
                        <Icon name="AlertCircle" size={12} />
                        <span>{riskSummary?.high} high</span>
                      </span>
                    )}
                  </div>
                )}

                {/* WIP Limit Warning */}
                {wipExceeded && (
                  <div className="mt-2 flex items-center space-x-1 text-xs text-red-700 bg-red-50 px-2 py-1 rounded">
                    <Icon name="AlertTriangle" size={12} />
                    <span>WIP limit exceeded</span>
                  </div>
                )}

                {/* Drop Feedback */}
                {draggedTask && !canDrop && hoveredColumn === column?.id && (
                  <div className="mt-2 flex items-center space-x-1 text-xs text-red-700 bg-red-50 px-2 py-1 rounded">
                    <Icon name="XCircle" size={12} />
                    <span>{dropMessage}</span>
                  </div>
                )}
              </div>
              {/* Droppable Column */}
              <Droppable droppableId={column?.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided?.innerRef}
                    {...provided?.droppableProps}
                    onMouseEnter={() => setHoveredColumn(column?.id)}
                    onMouseLeave={() => setHoveredColumn(null)}
                    className={`bg-gray-50 rounded-b-lg p-3 min-h-[500px] space-y-3 transition-colors ${
                      snapshot?.isDraggingOver && canDrop ? 'bg-blue-50 ring-2 ring-blue-300' : ''
                    } ${
                      snapshot?.isDraggingOver && !canDrop ? 'bg-red-50 ring-2 ring-red-300 opacity-60' : ''
                    } ${
                      draggedTask && !canDrop && !snapshot?.isDraggingOver ? 'opacity-40 bg-gray-100' : ''
                    }`}
                    role="list"
                    aria-label={`${column?.label} column`}
                  >
                    {columnTasks?.map((task, index) => {
                      const blocked = isTaskBlocked(task);
                      const blockReason = getBlockReason(task);
                      const riskBadge = getRiskBadge(task);
                      const overdue = isOverdue(task);
                      const progress = task?.progress || 0;

                      return (
                        <Draggable 
                          key={task?.id} 
                          draggableId={task?.id} 
                          index={index}
                          isDragDisabled={blocked && column?.id !== 'Blocked'}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided?.innerRef}
                              {...provided?.draggableProps}
                              {...provided?.dragHandleProps}
                              className={`bg-card rounded-lg shadow-sm border border-border p-4 cursor-pointer hover:shadow-md transition-all ${
                                getPriorityColor(task?.priority)
                              } ${
                                snapshot?.isDragging ? 'shadow-xl rotate-2 ring-2 ring-blue-400' : ''
                              } ${
                                blocked ? 'opacity-90 border-red-300 bg-red-50' : ''
                              }`}
                              onClick={() => onTaskClick(task)}
                              role="listitem"
                              aria-label={`Task: ${task?.title}`}
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e?.key === 'Enter' || e?.key === ' ') {
                                  e?.preventDefault();
                                  onTaskClick(task);
                                }
                              }}
                            >
                              {/* Task Card Content */}
                              <div className="space-y-3">
                                {/* Status Badges Row */}
                                <div className="flex flex-wrap gap-2">
                                  {/* Blocked/Dependency Badge */}
                                  {blocked && (
                                    <div 
                                      className="flex items-center space-x-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-1 rounded"
                                      title={blockReason}
                                    >
                                      <Icon name="Lock" size={12} />
                                      <span>{blockReason}</span>
                                    </div>
                                  )}

                                  {/* Risk Badge */}
                                  {riskBadge && (
                                    <div className={`flex items-center space-x-1 text-xs font-medium px-2 py-1 rounded ${riskBadge?.color}`}>
                                      <Icon name={riskBadge?.icon} size={12} />
                                      <span>{riskBadge?.label}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Title & ID */}
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900 line-clamp-2">
                                    {task?.title}
                                  </h4>
                                  <p className="text-xs text-gray-500 mt-1">{task?.id}</p>
                                </div>

                                {/* Progress Indicator */}
                                {progress > 0 && progress < 100 && (
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs text-gray-600">
                                      <span>Progress</span>
                                      <span className="font-medium">{progress}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                      <div 
                                        className="bg-blue-600 h-1.5 rounded-full transition-all"
                                        style={{ width: `${progress}%` }}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Priority & Dependencies */}
                                <div className="flex items-center justify-between">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    task?.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                                    task?.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                                    task?.priority === 'Medium'? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                                  }`}>
                                    {task?.priority}
                                  </span>
                                  {task?.dependencies?.length > 0 && (
                                    <div 
                                      className="flex items-center space-x-1 text-xs text-gray-600"
                                      title={`${task?.dependencies?.length} dependenc${task?.dependencies?.length !== 1 ? 'ies' : 'y'}`}
                                    >
                                      <Icon name="Link" size={12} />
                                      <span>{task?.dependencies?.length}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Ownership: Assignees */}
                                <div className="flex items-center space-x-2">
                                  <div className="flex items-center -space-x-2">
                                    {task?.assignees?.slice(0, 3)?.map((assignee) => (
                                      <div
                                        key={assignee?.id}
                                        className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-xs font-semibold border-2 border-white"
                                        title={`${assignee?.name} (${assignee?.email})`}
                                      >
                                        {assignee?.avatar}
                                      </div>
                                    ))}
                                    {task?.assignees?.length > 3 && (
                                      <div className="w-7 h-7 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center text-xs font-semibold border-2 border-white">
                                        +{task?.assignees?.length - 3}
                                      </div>
                                    )}
                                  </div>
                                  {task?.assignees?.length === 0 && (
                                    <span className="text-xs text-gray-400 italic">Unassigned</span>
                                  )}
                                </div>

                                {/* Project Context */}
                                {task?.projectId && (
                                  <div className="flex items-center space-x-1 text-xs text-gray-600">
                                    <Icon name="Folder" size={12} />
                                    <span>Project {task?.projectId}</span>
                                  </div>
                                )}

                                {/* Due Date */}
                                <div className="flex items-center justify-between text-xs">
                                  <div className={`flex items-center space-x-1 ${
                                    overdue ? 'text-red-600 font-semibold' : 'text-gray-600'
                                  }`}>
                                    <Icon name="Calendar" size={12} />
                                    <span>{formatDate(task?.dueDate)}</span>
                                    {overdue && (
                                      <span className="flex items-center space-x-0.5">
                                        <Icon name="AlertCircle" size={12} />
                                        <span className="font-medium">Overdue</span>
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Tags */}
                                {task?.tags?.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {task?.tags?.slice(0, 2)?.map((tag) => (
                                      <span
                                        key={tag}
                                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                    {task?.tags?.length > 2 && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                        +{task?.tags?.length - 2}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided?.placeholder}

                    {/* Empty State with Context */}
                    {columnTasks?.length === 0 && (
                      <div className="text-center py-12 text-gray-400">
                        <Icon name="Inbox" size={40} className="mx-auto mb-3 opacity-50" />
                        <p className="text-sm font-medium">No tasks</p>
                        <p className="text-xs mt-1 text-gray-500 px-4">{column?.description}</p>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default KanbanView;